import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { createConsumer } from "@rails/actioncable"
import { chatAttachmentSupport } from "../concerns/chat_attachment_support"

export default class extends chatAttachmentSupport(Controller) {
  static targets = ["contentArea", "chatInput", "fileInput", "attachmentPreview", "attachmentData", "uploadProgress", "sendButton", "dropZone"]
  static values = { selectedPartnerType: String, selectedPartnerId: Number, filter: String }

  get attachmentPreviewSizeClass() {
    return "h-12 w-12"
  }

  get attachmentDropActiveClasses() {
    return ["ring-2", "ring-blue-500", "ring-inset"]
  }

  connect() {
    this.pendingHighlightMessageId = null

    this.boundSubmitStart = () => {
      this.chatInputTarget.disabled = true
    }

    this.boundSubmitEnd = () => {
      this.chatInputTarget.disabled = false
      this.chatInputTarget.value = ""
      this.clearAttachment()
      this.chatInputTarget.focus()
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('chat:scroll-to-bottom', { detail: { smooth: true } }))
      }, 100)
    }

    document.addEventListener("turbo:submit-start", this.boundSubmitStart)
    document.addEventListener("turbo:submit-end", this.boundSubmitEnd)

    this.boundSelectConversation = this.onSelectConversation.bind(this)
    document.addEventListener('chat:select-conversation', this.boundSelectConversation)

    this.subscribe()
    this.dispatchActivity()
  }

  disconnect() {
    document.removeEventListener("turbo:submit-start", this.boundSubmitStart)
    document.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
    document.removeEventListener('chat:select-conversation', this.boundSelectConversation)
    this.unsubscribe()
  }

  selectConversation(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (event.button && event.button !== 0) return

    const partnerType = event.currentTarget.dataset.partnerType
    const partnerId = Number(event.currentTarget.dataset.partnerId)

    if (!partnerType || Number.isNaN(partnerId)) return

    event.preventDefault()
    this.switchConversation(partnerType, partnerId)
  }

  onSelectConversation(event) {
    const { partnerType, partnerId, messageId } = event.detail || {}
    if (!partnerType || !partnerId) return

    const sameConversation = (
      this.selectedPartnerTypeValue === partnerType &&
      Number(this.selectedPartnerIdValue) === partnerId
    )

    if (messageId) this.pendingHighlightMessageId = messageId

    if (sameConversation) {
      if (messageId) {
        document.dispatchEvent(new CustomEvent('chat:highlight-message', { detail: { messageId } }))
      }
      return
    }

    this.switchConversation(partnerType, partnerId)
  }

  async switchConversation(partnerType, partnerId) {
    if (!partnerType || !partnerId) return

    this.dispatchActivity()

    if (this.selectedPartnerTypeValue === partnerType && Number(this.selectedPartnerIdValue) === Number(partnerId)) {
      if (this.pendingHighlightMessageId) {
        document.dispatchEvent(new CustomEvent('chat:highlight-message', { detail: { messageId: this.pendingHighlightMessageId } }))
        this.pendingHighlightMessageId = null
      }
      return
    }

    const previousType = this.selectedPartnerTypeValue
    const previousId = this.selectedPartnerIdValue

    this.selectedPartnerTypeValue = partnerType
    this.selectedPartnerIdValue = Number(partnerId)

    const params = new URLSearchParams({
      partner_type: partnerType,
      partner_id: String(partnerId),
      sidebar_update: 'selection',
      switch: '1',
      filter: this.filterValue || 'active'
    })

    if (previousType && previousId) {
      params.append('prev_partner_type', previousType)
      params.append('prev_partner_id', String(previousId))
    }

    const url = `/admin/chats?${params.toString()}`
    const historyUrl = `/admin/chats?partner_type=${encodeURIComponent(partnerType)}&partner_id=${encodeURIComponent(partnerId)}`

    try {
      await get(url, { responseKind: 'turbo-stream' })
      document.dispatchEvent(new CustomEvent('chat:close-metadata'))
      this.clearAttachment()
      if (this.hasChatInputTarget) {
        this.chatInputTarget.value = ""
        this.chatInputTarget.focus()
      }
      if (this.pendingHighlightMessageId) {
        const messageId = this.pendingHighlightMessageId
        this.pendingHighlightMessageId = null
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('chat:highlight-message', { detail: { messageId } }))
        }, 120)
      } else {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('chat:scroll-to-bottom', { detail: { smooth: true } }))
        }, 100)
      }
      window.history.replaceState({}, '', historyUrl)
    } catch (error) {
      console.error('Conversation switch failed:', error)
      window.location.href = historyUrl
    }
  }

  subscribe() {
    if (!this.consumer) {
      this.consumer = createConsumer()
    }

    this.subscription = this.consumer.subscriptions.create({ channel: "ChatChannel" }, {
      received: (data) => {
        if (data && data.sender === 'admin') return

        const params = new URLSearchParams({
          partner_type: this.selectedPartnerTypeValue,
          partner_id: String(this.selectedPartnerIdValue)
        })

        get(`/admin/chats?${params.toString()}`, { responseKind: 'turbo-stream' }).then(() => {
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('chat:scroll-to-bottom', { detail: { smooth: true } }))
          }, 100)
        })
      }
    })
  }

  unsubscribe() {
    if (this.subscription) {
      try { this.subscription.unsubscribe() } catch (_) {}
      this.subscription = null
    }
  }

  triggerFileInput() {
    this.fileInputTarget.click()
  }

  dispatchActivity() {
    document.dispatchEvent(new CustomEvent('chat:activity'))
  }
}
